import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShieldCheck, Sun, Moon } from "lucide-react";
import { LOGO_URL } from "../../lib/api";
import { usePrefs } from "../../context/PrefsContext";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t, lang, setLang, theme, setTheme } = usePrefs();

  const NAV = [
    { label: t.nav.home, to: "/" },
    { label: t.nav.about, to: "/tentang-kami" },
    { label: t.nav.team, to: "/pasukan-kami" },
    { label: t.nav.services, to: "/perkhidmatan" },
    { label: t.nav.gallery, to: "/galeri" },
    { label: t.nav.contact, to: "/hubungi" },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20 gap-3">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              className="text-[#0b1533] hover:text-orange-600 transition-colors"
              onClick={() => setOpen(true)}
              data-testid="drawer-menu-toggle"
              aria-label="Menu"
            >
              <Menu size={26} />
            </button>
            <Link to="/" className="flex items-center" data-testid="header-logo">
              <img src={LOGO_URL} alt="Tanjung Marina Global" className="h-12 sm:h-14 w-auto" />
            </Link>
          </div>

          <div className="flex items-center gap-3 sm:gap-5">
            <nav className="hidden xl:flex items-center gap-6">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.to === "/" ? "utama" : item.to.slice(1)}`}
                  className={`text-sm font-semibold transition-colors ${
                    pathname === item.to ? "text-orange-600" : "text-slate-700 hover:text-orange-600"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="flex rounded-full border border-slate-300 overflow-hidden text-xs font-bold">
              <button
                onClick={() => setLang("bm")}
                data-testid="lang-bm-btn"
                className={`px-2.5 py-1.5 transition-colors ${lang === "bm" ? "bg-[#0b1533] text-white" : "text-slate-600 hover:text-orange-600"}`}
              >
                BM
              </button>
              <button
                onClick={() => setLang("en")}
                data-testid="lang-en-btn"
                className={`px-2.5 py-1.5 transition-colors ${lang === "en" ? "bg-[#0b1533] text-white" : "text-slate-600 hover:text-orange-600"}`}
              >
                EN
              </button>
            </div>

            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="theme-toggle-btn"
              aria-label="Tukar mod"
              className="w-9 h-9 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:text-orange-600 hover:border-orange-400 transition-colors shrink-0"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <Link
              to="/admin/login"
              data-testid="header-admin-login-btn"
              className="hidden sm:flex items-center gap-2 bg-[#0b1533] hover:bg-[#16225c] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all hover:-translate-y-0.5 whitespace-nowrap"
            >
              <ShieldCheck size={16} /> {t.nav.admin}
            </Link>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/50 z-[60]"
              data-testid="drawer-overlay"
            />
            <motion.aside
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: "tween", duration: 0.25 }}
              className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-[70] shadow-2xl flex flex-col"
              data-testid="drawer-menu"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
                <img src={LOGO_URL} alt="TMG" className="h-12 w-auto" />
                <button
                  onClick={() => setOpen(false)}
                  className="text-slate-500 hover:text-orange-600 transition-colors border border-slate-300 rounded p-1"
                  data-testid="drawer-close-btn"
                  aria-label="Tutup"
                >
                  <X size={20} />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto py-2">
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`drawer-link-${item.to === "/" ? "utama" : item.to.slice(1)}`}
                    className={`block px-6 py-4 text-sm font-semibold uppercase tracking-wider border-b border-slate-100 transition-colors ${
                      pathname === item.to
                        ? "bg-[#0b1533] text-white"
                        : "text-slate-700 hover:bg-slate-50 hover:text-orange-600"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  to="/admin/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-wider text-slate-700 hover:bg-slate-50 hover:text-orange-600 transition-colors"
                  data-testid="drawer-link-admin"
                >
                  <ShieldCheck size={15} /> {t.nav.admin}
                </Link>
              </nav>
              <div className="px-6 py-4 border-t border-slate-100 text-xs text-slate-400">
                © {new Date().getFullYear()} Tanjung Marina Global Sdn. Bhd.
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
